import { useMemo, useState, useRef, useCallback } from 'react';

interface Props {
  data: Array<{ ts: string; value: number }>;
  unit: string;
  lo: number;
  hi: number;
  color: string;
}

interface HoverPoint {
  svgX: number;
  svgY: number;
  value: number;
  ts: string;
}

const W = 720, H = 220;
const PAD = { top: 12, right: 14, bottom: 36, left: 52 };
const IW = W - PAD.left - PAD.right;
const IH = H - PAD.top - PAD.bottom;

function formatTs(ts: string) {
  const d = new Date(ts);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return {
    date: `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`,
    time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
  };
}

export default function HistoryChart({ data, unit, lo, hi, color }: Props) {
  const [hover, setHover] = useState<HoverPoint | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const computed = useMemo(() => {
    if (data.length === 0) return null;
    const values = data.map(d => d.value);
    const minVal = Math.min(...values, lo * 0.8);
    const maxVal = Math.max(...values, hi * 1.2);
    const range = maxVal - minVal || 1;

    const getY = (v: number) => PAD.top + IH - ((v - minVal) / range) * IH;
    const getX = (i: number) => PAD.left + (i / Math.max(data.length - 1, 1)) * IW;

    const xs = data.map((_, i) => getX(i));
    const ys = data.map(d => getY(d.value));
    const pts = xs.map((x, i) => `${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
    const areaPts = `${pts} ${PAD.left + IW},${PAD.top + IH} ${PAD.left},${PAD.top + IH}`;

    // X-axis labels
    const step = Math.max(1, Math.floor(data.length / 5));
    const xLabels = data
      .filter((_, i) => i === 0 || i === data.length - 1 || i % step === 0)
      .map(d => {
        const idx = data.indexOf(d);
        const t = new Date(d.ts);
        const label = `${t.getMonth() + 1}/${t.getDate()} ${String(t.getHours()).padStart(2, '0')}h`;
        return { x: xs[idx], label };
      });

    return { pts, areaPts, loY: getY(lo), hiY: getY(hi), xs, ys, xLabels };
  }, [data, lo, hi]);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || !computed || data.length < 2) return;
    const rect = svgRef.current.getBoundingClientRect();
    const rawIdx = ((e.clientX - rect.left) / rect.width * W - PAD.left) / IW * (data.length - 1);
    const idx = Math.max(0, Math.min(data.length - 1, Math.round(rawIdx)));
    setHover({ svgX: computed.xs[idx], svgY: computed.ys[idx], value: data[idx].value, ts: data[idx].ts });
  }, [computed, data]);

  if (!computed) return null;
  const { pts, areaPts, loY, hiY, xLabels } = computed;

  // Tooltip layout
  const TW = 116, TH = 56;
  let tooltipX = 0, tooltipY = 0, label = { date: '', time: '' };
  if (hover) {
    tooltipX = hover.svgX + 14 + TW > W - PAD.right ? hover.svgX - TW - 14 : hover.svgX + 14;
    tooltipY = Math.max(PAD.top, Math.min(hover.svgY - TH / 2, PAD.top + IH - TH));
    label = formatTs(hover.ts);
  }

  return (
    <svg
      ref={svgRef}
      width="100%" height="auto"
      viewBox={`0 0 ${W} ${H}`}
      style={{ background: 'var(--white)', borderRadius: 8, cursor: 'crosshair', display: 'block' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHover(null)}
    >
      <defs>
        <linearGradient id="chartGradient" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.12" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
        <line key={i}
          x1={PAD.left} y1={PAD.top + IH * p}
          x2={PAD.left + IW} y2={PAD.top + IH * p}
          stroke="#f0f0ec" strokeWidth="1"
        />
      ))}

      {/* Optimal range band */}
      <rect x={PAD.left} y={hiY} width={IW} height={Math.abs(loY - hiY)}
        fill="var(--green)" fillOpacity="0.04" />

      {/* Threshold lines */}
      <line x1={PAD.left} y1={loY} x2={PAD.left + IW} y2={loY}
        stroke="var(--amber)" strokeWidth="1" strokeDasharray="3,2" />
      <line x1={PAD.left} y1={hiY} x2={PAD.left + IW} y2={hiY}
        stroke="var(--red)" strokeWidth="1" strokeDasharray="3,2" />

      {/* Threshold labels */}
      <text x={PAD.left - 6} y={loY} fontSize="9" fill="var(--amber)" textAnchor="end" dominantBaseline="middle">{lo}</text>
      <text x={PAD.left - 6} y={hiY} fontSize="9" fill="var(--red)" textAnchor="end" dominantBaseline="middle">{hi}</text>

      {/* Area fill */}
      <polygon points={areaPts} fill="url(#chartGradient)" />

      {/* Data line */}
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2"
        strokeLinejoin="round" strokeLinecap="round" />

      {/* X-axis labels */}
      {xLabels.map(({ x, label: lbl }) => (
        <text key={x} x={x} y={H - 8} textAnchor="middle" fontSize="9" fill="var(--gray)">{lbl}</text>
      ))}

      {/* Y-axis unit */}
      <text x={12} y={PAD.top + IH / 2} fontSize="9" fill="var(--gray)"
        transform={`rotate(-90, 12, ${PAD.top + IH / 2})`} textAnchor="middle">{unit}</text>

      {/* Invisible hit area */}
      <rect x={PAD.left} y={PAD.top} width={IW} height={IH} fill="transparent" />

      {/* Crosshair */}
      {hover && (
        <>
          <line
            x1={hover.svgX} y1={PAD.top}
            x2={hover.svgX} y2={PAD.top + IH}
            stroke="var(--dark)" strokeWidth="1"
            strokeDasharray="3,3" opacity="0.3"
          />
          <circle cx={hover.svgX} cy={hover.svgY} r="5"
            fill="var(--white)" stroke={color} strokeWidth="2.5" />

          {/* Tooltip */}
          <rect x={tooltipX} y={tooltipY} width={TW} height={TH} rx="7"
            fill="#0d1f0f" opacity="0.9" />
          <text x={tooltipX + TW / 2} y={tooltipY + 15}
            textAnchor="middle" fontSize="10" fill="#9ee8c4" fontWeight="600">
            {label.date}
          </text>
          <text x={tooltipX + TW / 2} y={tooltipY + 27}
            textAnchor="middle" fontSize="10" fill="#888">
            {label.time}
          </text>
          <line x1={tooltipX + 10} y1={tooltipY + 33} x2={tooltipX + TW - 10} y2={tooltipY + 33}
            stroke="#ffffff" strokeOpacity="0.1" strokeWidth="1" />
          <text x={tooltipX + TW / 2} y={tooltipY + 47}
            textAnchor="middle" fontSize="13" fill={color} fontWeight="700">
            {hover.value.toFixed(1)} {unit}
          </text>
        </>
      )}
    </svg>
  );
}
